import { Alert, Card as AntdCard, Col, Pagination, Row, message, Spin } from 'antd'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import axios from 'axios'
import { api } from '../../../../../services'
import { config } from '../../../../../constants'
import { getToken, getUser } from '../../../../../services/api/user'
import TrainingListActions from '../components/TrainingListActions'
import TrainingListContainer from '../components/TrainingListContainer'
import ShortsPlayer from '../../../../Library/internal/DocumentManagement/ShortsPlayer'
import { types } from '../types'
import { useTranslation } from 'react-i18next'

const PAGE_LIMIT = 6

function ShortsList() {
    const [shorts, setShorts] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [search, setSearch] = useState('')
    const [pageOffset, setPageOffset] = useState(0)
    const [total, setTotal] = useState(0)
    const [isInitialLoad, setIsInitialLoad] = useState(true)
    const [seqList, setSeqList] = useState([])

    // Like/Dislike related states
    const [shlToken, setShlToken] = useState(null)
    const [userData, setUserData] = useState(null)
    const [videoReactions, setVideoReactions] = useState({})
    const [videoCounts, setVideoCounts] = useState({})
    const [loadingLikes, setLoadingLikes] = useState({})
    const [loadingDislikes, setLoadingDislikes] = useState({})
    const [enableModal, setEnableModal] = React.useState(false);
    const [selectedVideo, setSelectedVideo] = React.useState(null);

    // Thumbnail and hover video states
    const [thumbnails, setThumbnails] = useState({})
    const [hoveredVideo, setHoveredVideo] = useState(null)
    const [loadingVideo, setLoadingVideo] = useState(null)
    const [loadedVideos, setLoadedVideos] = useState(new Set())
    const [failedVideos, setFailedVideos] = useState(new Set())

    // Refs for timeout management
    const hoverTimeoutRef = useRef({})
    const loadTimeoutRef = useRef({})

    // Fetch token on component mount
    useEffect(() => {
        const fetchToken = async () => {
            try {
                const token = await getToken();
                const user = await getUser();
                console.log("User in list", user);
                setShlToken(token);
                setUserData(user)
            } catch (err) {
                console.error("Error fetching SHL token:", err);
            }
        };

        fetchToken();
    }, []);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            Object.values(hoverTimeoutRef.current).forEach(clearTimeout)
            Object.values(loadTimeoutRef.current).forEach(clearTimeout)
        }
    }, [])

    // Fetch thumbnails for each video
    useEffect(() => {
        async function fetchThumbnails() {
            const newThumbs = {};
            for (const video of shorts || []) {
                if (!video.link) continue;
                const match = /vimeo.*\/(\d+)/.exec(video.link);
                if (!match) continue;
                const videoId = match[1];
                try {
                    const res = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}&width=360&height=640`);
                    const json = await res.json();
                    if (json.thumbnail_url) {
                        newThumbs[video.tip.id] = json.thumbnail_url;
                    }
                } catch (err) {
                    console.error("Thumbnail fetch error:", err);
                }
            }
            setThumbnails(prev => ({ ...prev, ...newThumbs }));
        }
        if (shorts.length > 0) {
            fetchThumbnails();
        }
    }, [shorts]);

    const getShortsData = (searchValue = '') => {
        if (!loading) setLoading(true);

        api.library
            .getSopList({
                offset: pageOffset,
                limit: PAGE_LIMIT,
                search: searchValue,
                isOrg: true,
                isTip: true,
                order: 'created_at',
                isApproved: true
            })
            .then(res => {
                console.log("response shorts api: ", res.data);

                // Check if response exists and has the expected structure
                if (!res || !res.data) {
                    console.warn("No response data received");
                    setShorts([]);
                    setTotal(0);
                    setLoading(false);
                    setError(true);
                    return;
                }

                // Check if data has the expected properties
                const { count = 0, rows = [] } = res.data;

                // Validate that rows is an array
                if (!Array.isArray(rows)) {
                    console.warn("Response rows is not an array:", rows);
                    setShorts([]);
                    setTotal(0);
                    setLoading(false);
                    setError(true);
                    return;
                }

                console.log("All filtered tips data: ", rows);
                console.log("Total count: ", count);

                // Set the data
                setTotal(count);
                setShorts(rows);

                // Optional: Check if no data found
                if (rows.length === 0) {
                    console.log("No shorts data found");
                    // You might want to show a "no data" message instead of error
                    // setError(true); // Uncomment if you want to show error for no data
                }

                setLoading(false);
                setError(false);
            })
            .catch(err => {
                console.error("Error fetching shorts data:", err);

                // More detailed error logging
                if (err.response) {
                    console.error("Error status:", err.response.status);
                    console.error("Error data:", err.response.data);
                } else if (err.request) {
                    console.error("No response received:", err.request);
                } else {
                    console.error("Request setup error:", err.message);
                }

                // Reset states on error
                setShorts([]);
                setTotal(0);
                setLoading(false);
                setError(true);
            });
    };

    useEffect(() => {
        getShortsData(search)
    }, [pageOffset, search])

    const handlePagination = (pageNumber) => {
        setPageOffset((pageNumber - 1) * PAGE_LIMIT)
    }

    const handleSearch = (searchValue) => {
        setPageOffset(0)
        setSearch(searchValue)
    }

    const onCardClick = (vid) => {
        setSelectedVideo(vid);
        setEnableModal(true);
    }

    const handleNextVideo = () => {
        if (selectedVideo && shorts && shorts.length > 0) {
            // Find current video index in the data array
            const currentIndex = shorts.findIndex(video => video.id === selectedVideo.id);

            // If current video is found and not the last one
            if (currentIndex !== -1 && currentIndex < shorts.length - 1) {
                setSelectedVideo(shorts[currentIndex + 1]);
            }
            // Optional: Loop back to first video if at the end
            // else if (currentIndex === data.length - 1) {
            //   setSelectedVideo(data[0]);
            // }
        }
    };

    const handlePrevVideo = () => {
        if (selectedVideo && shorts && shorts.length > 0) {
            // Find current video index in the data array
            const currentIndex = shorts.findIndex(video => video.id === selectedVideo.id);

            // If current video is found and not the first one
            if (currentIndex !== -1 && currentIndex > 0) {
                setSelectedVideo(shorts[currentIndex - 1]);
            }
            // Optional: Loop to last video if at the beginning
            // else if (currentIndex === 0) {
            //   setSelectedVideo(data[data.length - 1]);
            // }
        }
    };

    // Enhanced mouse hover handlers - FIXED VERSION
    const handleMouseEnter = (videoId) => {
        // Clear any existing timeout for this video
        if (hoverTimeoutRef.current[videoId]) {
            clearTimeout(hoverTimeoutRef.current[videoId]);
            delete hoverTimeoutRef.current[videoId];
        }

        // Clear loading timeout if exists
        if (loadTimeoutRef.current[videoId]) {
            clearTimeout(loadTimeoutRef.current[videoId]);
            delete loadTimeoutRef.current[videoId];
        }

        // Set hover state immediately
        setHoveredVideo(videoId);

        // Only proceed if video hasn't failed and isn't already loaded
        if (!failedVideos.has(videoId)) {
            // Set loading state with a small delay to avoid flicker
            const hoverDelay = setTimeout(() => {
                setLoadingVideo(videoId);
                
                // Set a timeout to handle cases where video doesn't load
                loadTimeoutRef.current[videoId] = setTimeout(() => {
                    console.warn(`Video ${videoId} failed to load within timeout`);
                    setLoadingVideo(prev => prev === videoId ? null : prev);
                    setFailedVideos(prev => new Set(prev).add(videoId));
                    
                    // Clean up timeout reference
                    if (loadTimeoutRef.current[videoId]) {
                        delete loadTimeoutRef.current[videoId];
                    }
                }, 5000); // 5 second timeout
            }, 200); // 200ms delay before showing loading

            hoverTimeoutRef.current[videoId] = hoverDelay;
        }
    };

    const handleMouseLeave = (videoId) => {
        // Clear any pending hover timeout
        if (hoverTimeoutRef.current[videoId]) {
            clearTimeout(hoverTimeoutRef.current[videoId]);
            delete hoverTimeoutRef.current[videoId];
        }

        // Clear loading timeout
        if (loadTimeoutRef.current[videoId]) {
            clearTimeout(loadTimeoutRef.current[videoId]);
            delete loadTimeoutRef.current[videoId];
        }

        // Use a small delay to prevent flickering when moving between elements
        const leaveTimeout = setTimeout(() => {
            setHoveredVideo(prev => prev === videoId ? null : prev);
            setLoadingVideo(prev => prev === videoId ? null : prev);
        }, 100);

        hoverTimeoutRef.current[videoId] = leaveTimeout;
    };

    const handleVideoLoad = (videoId) => {
        console.log(`Video ${videoId} loaded successfully`);
        
        // Clear the timeout for this video
        if (loadTimeoutRef.current[videoId]) {
            clearTimeout(loadTimeoutRef.current[videoId]);
            delete loadTimeoutRef.current[videoId];
        }

        // Mark video as loaded and clear loading state
        setLoadedVideos(prev => new Set(prev).add(videoId));
        setLoadingVideo(prev => prev === videoId ? null : prev);
    };

    const handleVideoError = (videoId) => {
        console.error(`Video ${videoId} failed to load`);
        
        // Clear the timeout for this video
        if (loadTimeoutRef.current[videoId]) {
            clearTimeout(loadTimeoutRef.current[videoId]);
            delete loadTimeoutRef.current[videoId];
        }

        // Mark video as failed and clear loading state
        setFailedVideos(prev => new Set(prev).add(videoId));
        setLoadingVideo(prev => prev === videoId ? null : prev);
    };

    // API call function for like/dislike
    const handleReaction = async (videoId, reaction) => {
        // Check if user has already reacted to this video
        // if (videoReactions[videoId]) {
        //     message.warning(`You have already ${videoReactions[videoId]}d this video`);
        //     return;
        // }

        // Set appropriate loading state
        if (reaction === 'like') {
            setLoadingLikes(prev => ({ ...prev, [videoId]: true }));
        } else {
            setLoadingDislikes(prev => ({ ...prev, [videoId]: true }));
        }

        try {
            const response = await axios.patch(
                `${config.baseURL}library/tip/${videoId}/react`,
                { reaction: reaction },
                {
                    headers: {
                        'Authorization': `Bearer ${shlToken}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.status === 200 && response.data.success) {
                // Update the reaction state to prevent further reactions
                setVideoReactions(prev => ({
                    ...prev,
                    [videoId]: reaction
                }));

                // Update the counts from API response
                setVideoCounts(prev => ({
                    ...prev,
                    [videoId]: {
                        likes_count: response.data.likes_count,
                        dislikes_count: response.data.dislikes_count
                    }
                }));

                // message.success(`Successfully ${reaction}d the video!`);
            }
        } catch (error) {
            console.error('Error reacting to video:', error);

            if (error.response?.status === 401) {
                message.error('Authentication failed. Please login again.');
            } else if (error.response?.status === 404) {
                message.error('Video not found.');
            } else if (error.response?.status === 409) {
                message.warning('You have already reacted to this video.');
                // Update state to reflect that user has already reacted
                setVideoReactions(prev => ({
                    ...prev,
                    [videoId]: reaction
                }));
            } else {
                message.error('Failed to react to video. Please try again.');
            }
        } finally {
            // Clear appropriate loading state
            if (reaction === 'like') {
                setLoadingLikes(prev => ({ ...prev, [videoId]: false }));
            } else {
                setLoadingDislikes(prev => ({ ...prev, [videoId]: false }));
            }
        }
    };

    const handleLike = (e, videoId) => {
        e.stopPropagation(); // Prevent card click
        handleReaction(videoId, 'like');
    };

    const handleDislike = (e, videoId) => {
        e.stopPropagation(); // Prevent card click
        handleReaction(videoId, 'dislike');
    };

    const currentPageNumber = useMemo(() => Math.floor(pageOffset / PAGE_LIMIT) + 1, [pageOffset])

     console.log("Shorts State Data: ", shorts);

    const showItems = !loading && shorts && shorts.length > 0

    console.log("Shorts State Data: ", showItems);

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A'
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        })
    }

    // Helper function to get video duration from filename or set default
    const getVideoDuration = (filename) => {
        // You can implement logic to extract duration from filename or metadata
        // For now, returning a default duration
        return Math.floor(Math.random() * 45) + 15
    }

    // Helper function to get current counts (from API response or original data)
    const getCurrentCounts = (video) => {
        const apiCounts = videoCounts[video.tip.id];
        if (apiCounts) {
            return apiCounts;
        }
        return {
            likes_count: video.tip?.likes_count || 0,
            dislikes_count: video.tip?.dislikes_count || 0
        };
    };

    // Helper function to get optimized video URL for autoplay
    const getOptimizedVideoUrl = (videoLink) => {
        if (!videoLink) return '';

        // Extract video ID from Vimeo URL
        const match = /vimeo.*\/(\d+)/.exec(videoLink);
        if (!match) return videoLink;
        
        const videoId = match[1];
        
        // Build optimized URL for hover autoplay
        const baseUrl = `https://player.vimeo.com/video/${videoId}`;
        const params = new URLSearchParams({
            autoplay: '1',
            muted: '0',
            loop: '1',
            background: '1',
            controls: '0',
            title: '0',
            byline: '0',
            portrait: '0',
            transparent: '0',
            responsive: '1',
            playsinline: '1', // Important for mobile browsers
            dnt: '1' // Do not track
        });

        return `${baseUrl}?${params.toString()}`;
    };

    return (
        <TrainingListContainer title="Shorts">
            <TrainingListActions
                type={types.SHORTS}
                loading={loading}
                onListSearch={handleSearch}
            />

            {/* VIDEO TILES */}
            {error && (
                <Alert
                    message="Error"
                    description="Failed to load shorts videos"
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            {showItems && (
                <React.Fragment>
                    <Row gutter={[16, 16]} style={{ marginBottom: 24, padding: '16px' }}>
                        {shorts && shorts.length > 0 && shorts.map((video, index) => {
                            const currentCounts = getCurrentCounts(video);
                            const hasReacted = videoReactions[video.id];
                            const videoId = video.tip.id;
                            const isVideoHovered = hoveredVideo === videoId;
                            const isVideoLoading = loadingVideo === videoId;
                            const hasVideoFailed = failedVideos.has(videoId);
                            const hasVideoLoaded = loadedVideos.has(videoId);

                            return (
                                <Col xs={24}
                                    sm={12}
                                    style={{
                                        flex: '0 0 20%',   // 20% each = 5 tiles
                                        maxWidth: '20%'
                                    }}
                                    key={video.id || index}>
                                    <AntdCard
                                        hoverable
                                        style={{
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s'
                                        }}
                                        bodyStyle={{ padding: 12 }}
                                        onClick={() => onCardClick(video)}
                                    >
                                        {/* Video Thumbnail */}
                                        <div
                                            style={{
                                                position: 'relative',
                                                width: '100%',
                                                height: 230,
                                                background: 'linear-gradient(135deg, #36373cff 0%, #464349ff 100%)',
                                                borderRadius: 8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginBottom: 12,
                                                overflow: 'hidden'
                                            }}
                                            onMouseEnter={() => handleMouseEnter(videoId)}
                                            onMouseLeave={() => handleMouseLeave(videoId)}
                                        >
                                            {isVideoHovered && video.link && !hasVideoFailed ? (
                                                <>
                                                    {isVideoLoading && (
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                background: 'linear-gradient(135deg, #515256ff 0%, #3d3a41ff 100%)',
                                                                zIndex: 3
                                                            }}
                                                        >
                                                            <Spin size="large" style={{ color: "#fff" }} />
                                                        </div>
                                                    )}
                                                    <iframe
                                                        key={`video-${videoId}`} // Force re-render with key
                                                        src={getOptimizedVideoUrl(video.link)}
                                                        frameBorder="0"
                                                        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                                                        allowFullScreen={false}
                                                        onLoad={() => handleVideoLoad(videoId)}
                                                        onError={() => handleVideoError(videoId)}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            backgroundColor: 'transparent',
                                                            border: 'none',
                                                            outline: 'none',
                                                            pointerEvents: 'none' // Prevent iframe from interfering with hover
                                                        }}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    {thumbnails[videoId] ? (
                                                        <div
                                                            style={{
                                                                //position: 'relative',
                                                                width: '100%',
                                                                padding: '100%', // 16/9 → portrait (height > width)                             
                                                                backgroundColor: '#000',
                                                                borderRadius: 2,
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <img
                                                                src={thumbnails[videoId]}
                                                                alt={video.title}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    //objectFit: 'contain',   // keeps full image
                                                                    //objectPosition: 'center',
                                                                }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        /* Play Button */
                                                        <div
                                                            style={{
                                                                width: 50,
                                                                height: 50,
                                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                                borderRadius: '50%',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                                transition: 'all 0.3s ease',
                                                                cursor: 'pointer'
                                                            }}
                                                            className="play-button"
                                                        >
                                                            <div
                                                                style={{
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderLeft: '15px solid #666',
                                                                    borderTop: '10px solid transparent',
                                                                    borderBottom: '10px solid transparent',
                                                                    marginLeft: 3
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Approved Badge */}
                                            {video.tip?.status === 'approved' && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        backgroundColor: '#52c41a',
                                                        color: 'white',
                                                        padding: '4px 8px',
                                                        borderRadius: 12,
                                                        fontSize: 10,
                                                        fontWeight: 'bold',
                                                        textTransform: 'uppercase',
                                                        zIndex: 4
                                                    }}
                                                >
                                                    APPROVED
                                                </div>
                                            )}

                                            {/* Duration Badge */}
                                            {/* <div
                                                style={{
                                                    position: 'absolute',
                                                    bottom: 8,
                                                    right: 8,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                    color: 'white',
                                                    padding: '2px 6px',
                                                    borderRadius: 4,
                                                    fontSize: 10
                                                }}
                                            >
                                                {getVideoDuration(video.filename)}s
                                            </div> */}
                                        </div>

                                        {/* Video Category */}
                                        <div
                                            style={{
                                                backgroundColor: '#1890ff',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: 16,
                                                fontSize: 12,
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase',
                                                display: 'inline-block',
                                                marginBottom: 8
                                            }}
                                        >
                                            {'SHORTS'}
                                        </div>

                                        {/* Video Title */}
                                        <h3
                                            style={{
                                                fontSize: 16,
                                                fontWeight: 600,
                                                color: '#333',
                                                marginBottom: 8,
                                                lineHeight: 1.3,
                                                minHeight: 42,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                margin: '0 0 8px 0'
                                            }}
                                        >
                                            {video.title || video.filename || 'Untitled Video'}
                                        </h3>

                                        {/* Video Date */}
                                        <p
                                            style={{
                                                fontSize: 12,
                                                color: '#999',
                                                marginBottom: 8,
                                                margin: '0 0 8px 0'
                                            }}
                                        >
                                            {formatDate(video.created_at)}
                                        </p>

                                        {/* Video Author */}
                                        <p
                                            style={{
                                                fontSize: 12,
                                                color: '#1890ff',
                                                marginBottom: 8,
                                                margin: '0 0 8px 0'
                                            }}
                                        >
                                            By {video.creator?.username || video.creator?.name || 'Unknown'}
                                        </p>

                                        {/* Video Stats */}
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                //gap: 4
                                            }}
                                        >
                                            {/* Like Button */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 4,
                                                    cursor: hasReacted ? 'not-allowed' : 'pointer',
                                                    opacity: hasReacted && hasReacted !== 'like' ? 0.5 : 1,
                                                    transition: 'all 0.2s',
                                                    borderRadius: 2,
                                                    backgroundColor: hasReacted === 'like' ? '#f6ffed' : 'transparent',
                                                    border: hasReacted === 'like' ? '1px solid #52c41a' : '1px solid transparent'
                                                }}
                                                onClick={(e) => !hasReacted && handleLike(e, video.tip.id)}
                                                onMouseEnter={(e) => {
                                                    if (!hasReacted) {
                                                        e.currentTarget.style.backgroundColor = '#f6ffed';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!hasReacted) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                {loadingLikes[video.tip.id] ? (
                                                    <div
                                                        style={{
                                                            width: 14,
                                                            height: 14,
                                                            border: '2px solid #f3f3f3',
                                                            borderTop: '2px solid #52c41a',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }}
                                                    />
                                                ) : (
                                                    <span
                                                        style={{
                                                            fontSize: 14,
                                                            color: hasReacted === 'like' ? '#52c41a' : '#52c41a'
                                                        }}
                                                    >
                                                        👍
                                                    </span>
                                                )}
                                                <span style={{
                                                    fontSize: 12,
                                                    color: hasReacted === 'like' ? '#52c41a' : '#999',
                                                    fontWeight: hasReacted === 'like' ? 'bold' : 'normal'
                                                }}>
                                                    {currentCounts.likes_count}
                                                </span>
                                            </div>

                                            {/* Dislike Button */}
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                    cursor: hasReacted ? 'not-allowed' : 'pointer',
                                                    opacity: hasReacted && hasReacted !== 'dislike' ? 0.5 : 1,
                                                    transition: 'all 0.2s',
                                                    padding: '4px 8px',
                                                    borderRadius: 4,
                                                    backgroundColor: hasReacted === 'dislike' ? '#fff2f0' : 'transparent',
                                                    border: hasReacted === 'dislike' ? '1px solid #ff4d4f' : '1px solid transparent'
                                                }}
                                                onClick={(e) => !hasReacted && handleDislike(e, video.tip.id)}
                                                onMouseEnter={(e) => {
                                                    if (!hasReacted) {
                                                        e.currentTarget.style.backgroundColor = '#fff2f0';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!hasReacted) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                {loadingDislikes[video.tip.id] ? (
                                                    <div
                                                        style={{
                                                            width: 14,
                                                            height: 14,
                                                            border: '2px solid #f3f3f3',
                                                            borderTop: '2px solid #ff4d4f',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }}
                                                    />
                                                ) : (
                                                    <span
                                                        style={{
                                                            fontSize: 14,
                                                            color: hasReacted === 'dislike' ? '#ff4d4f' : '#ff4d4f'
                                                        }}
                                                    >
                                                        👎
                                                    </span>
                                                )}
                                                <span style={{
                                                    fontSize: 12,
                                                    color: hasReacted === 'dislike' ? '#ff4d4f' : '#999',
                                                    fontWeight: hasReacted === 'dislike' ? 'bold' : 'normal'
                                                }}>
                                                    {currentCounts.dislikes_count}
                                                </span>
                                            </div>
                                        </div>
                                    </AntdCard>
                                </Col>
                            )
                        })}
                    </Row>

                    {/* Pagination */}
                    {total > PAGE_LIMIT && (
                        <Row justify="center">
                            <Col>
                                <Pagination
                                    current={currentPageNumber}
                                    total={total}
                                    pageSize={PAGE_LIMIT}
                                    onChange={handlePagination}
                                    showSizeChanger={false}
                                    showQuickJumper={false}
                                    showTotal={(total, range) =>
                                        `${range[0]}-${range[1]} of ${total} videos`
                                    }
                                    style={{ textAlign: 'center', padding: '16px' }}
                                />
                            </Col>
                        </Row>
                    )}
                </React.Fragment>
            )}

            {/* ShortsPlayer Modal */}
            {enableModal && selectedVideo && (
                <ShortsPlayer
                    defaultQuery={null}
                    isAdmin={false}
                    visible={enableModal}
                    item={selectedVideo}
                    shlToken={shlToken}
                    onCancelTips={() => setEnableModal(false)}
                    onNext={handleNextVideo}
                    onPrev={handlePrevVideo}
                    hasNext={true}
                    hasPrev={true}
                    hideButton={true}
                    userData={userData}
                />
            )}

            {!loading && shorts.length === 0 && !error && (
                <div
                    style={{
                        textAlign: 'center',
                        padding: 48,
                        color: '#999'
                    }}
                >
                    <h3 style={{ color: '#999', marginBottom: 8 }}>No shorts videos found</h3>
                    <p style={{ color: '#999', margin: 0 }}>Try adjusting your search criteria</p>
                </div>
            )}

            {loading && (
                <div
                    style={{
                        textAlign: 'center',
                        padding: 48,
                        //color: '#999'
                    }}
                >
                    <div
                        style={{
                            display: 'inline-block',
                            width: 20,
                            height: 20,
                            border: '3px solid #f3f3f3',
                            borderTop: '3px solid #1890ff',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            marginBottom: 16
                        }}
                    />
                    <h3 style={{ color: '#999', margin: 0 }}>Loading shorts videos...</h3>
                </div>
            )}

            {/* CSS for animations */}
            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .play-button:hover {
                    transform: scale(1.1);
                    backgroundColor: rgba(255, 255, 255, 1) !important;
                }
            `}</style>
        </TrainingListContainer>
    )
}

export default ShortsList